import React, { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Password input with a show/hide ("eyeball") toggle.
 *
 * Defaults to hidden (type="password"). Clicking the toggle reveals the value
 * (type="text") and clicking again hides it. All extra props (data-testid,
 * id, value, onChange, onKeyDown, autoComplete, etc.) are forwarded to the
 * underlying Input so it is a drop-in replacement for a password <Input/>.
 */
export const PasswordInput = forwardRef(
    ({ className, toggleTestId, ...props }, ref) => {
        const [visible, setVisible] = useState(false);
        return (
            <div className="relative">
                <Input
                    ref={ref}
                    type={visible ? "text" : "password"}
                    className={cn("pr-11", className)}
                    {...props}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setVisible((v) => !v)}
                    data-testid={toggleTestId || "password-visibility-toggle"}
                    aria-label={visible ? "Hide password" : "Show password"}
                    aria-pressed={visible}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-white/45 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/50"
                >
                    {visible ? (
                        <EyeOff className="h-4 w-4" />
                    ) : (
                        <Eye className="h-4 w-4" />
                    )}
                </button>
            </div>
        );
    },
);

PasswordInput.displayName = "PasswordInput";
